import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Select, Tag } from 'antd';
import { TUser } from '../../../types/user';
import styled from 'styled-components';
import { formatMentions } from '../../../utils/formatMentions';
import { Role } from '../../../utils/constants';

const { Option } = Select;

const TextAreaWrapper = styled.div`
  position: relative;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;

  &:hover {
    border-color: #40a9ff;
  }

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const MentionedUsersContainer = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HelpText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #8c8c8c;
`;

interface MentionTextAreaProps {
  value: string;
  onChange: (value: string, mentionedUserIds: string[]) => void;
  users: TUser[];
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  showMentionSelector?: boolean;
}

export const MentionTextArea: React.FC<MentionTextAreaProps> = ({
  value,
  onChange,
  users,
  placeholder = 'Type your note here. Use the dropdown below to @mention someone...',
  disabled = false,
  rows = 4,
  showMentionSelector = true,
}) => {
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [selectedUsersForMention, setSelectedUsersForMention] = useState<string[]>([]);
  const [displayValue, setDisplayValue] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Extract mentioned user IDs from text when value changes
  useEffect(() => {
    const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const ids: string[] = [];
    let match;

    while ((match = mentionPattern.exec(value)) !== null) {
      ids.push(match[2]);
    }

    setMentionedUserIds(ids);

    // Update display value to show clean mentions
    setDisplayValue(formatMentions(value));
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDisplayValue = e.target.value;

    // Need to reconstruct the actual value with full mention format
    // If user is typing normally, just use their text
    // The mentions will be preserved from the previous value
    let newValue = newDisplayValue;

    // If we have mentions, we need to restore the full format
    if (mentionedUserIds.length > 0) {
      // For each mentioned user, replace @Name with @[Name](id)
      mentionedUserIds.forEach((userId) => {
        const user = users.find((u) => u.id === userId);
        if (user) {
          const shortMention = `@${user.firstName} ${user.lastName}`;
          const fullMention = `@[${user.firstName} ${user.lastName}](${userId})`;

          // Only replace if the short mention exists in the new value
          if (newDisplayValue.includes(shortMention)) {
            // Use replaceAll to replace all occurrences
            newValue = newValue.replaceAll(shortMention, fullMention);
          }
        }
      });
    }

    setDisplayValue(newDisplayValue);
    onChange(newValue, mentionedUserIds);
  };

  const handleMentionSelect = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    // Create mention text
    const mentionText = `@[${user.firstName} ${user.lastName}](${user.id})`;

    // Insert at cursor position or append
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const textBefore = value.substring(0, start);
      const textAfter = value.substring(end);

      // Add space before mention if needed
      const needsSpaceBefore = textBefore.length > 0 && !textBefore.endsWith(' ') && !textBefore.endsWith('\n');
      const prefix = needsSpaceBefore ? ' ' : '';

      const newValue = textBefore + prefix + mentionText + ' ' + textAfter;

      // Update value
      onChange(newValue, [...mentionedUserIds, userId]);

      // Set cursor position after mention
      setTimeout(() => {
        const newCursorPos = start + prefix.length + mentionText.length + 1;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    } else {
      // Fallback: append to end
      const needsSpace = value.length > 0 && !value.endsWith(' ') && !value.endsWith('\n');
      const newValue = value + (needsSpace ? ' ' : '') + mentionText + ' ';
      onChange(newValue, [...mentionedUserIds, userId]);
    }

    // Clear selection
    setSelectedUsersForMention([]);
  };

  const handleRemoveMention = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    // Remove mention from text
    const mentionPattern = new RegExp(`@\\[${user.firstName} ${user.lastName}\\]\\(${userId}\\)\\s?`, 'g');
    const newValue = value.replace(mentionPattern, '');

    const newMentionedIds = mentionedUserIds.filter((id) => id !== userId);
    onChange(newValue, newMentionedIds);
  };

  // Helper function to get role label
  const getRoleLabel = (user: TUser): string | null => {
    if (user.role === Role.SUPER_ADMIN) return 'Super Admin';
    if (user.role === Role.ADMIN) return 'Admin';
    if (user.role === Role.LEAD) return 'Lead';

    // For staff, show designation badges
    if (user.role === Role.STAFF && user.staff) {
      const badges = [];
      if (user.staff.lead) badges.push('L');
      if (user.staff.driver) badges.push('D');
      if (badges.length > 0) {
        return `Staff (${badges.join(', ')})`;
      }
      return 'Staff';
    }

    return null;
  };

  // Show all users passed in (filtering is done by the parent component)
  const filteredUsers = useMemo(() => {
    return users;
  }, [users]);

  // Get users who are mentioned in the text
  const mentionedUsers = filteredUsers.filter((user) =>
    mentionedUserIds.includes(user.id)
  );

  // Filter out users who are already mentioned
  const availableUsers = filteredUsers.filter(
    (user) => !mentionedUserIds.includes(user.id)
  );

  return (
    <div>
      <TextAreaWrapper>
        <StyledTextArea
          ref={textareaRef}
          value={displayValue}
          onChange={handleTextChange}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
        />
      </TextAreaWrapper>

      {showMentionSelector && availableUsers.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="@ Mention users (type to search)..."
            value={selectedUsersForMention}
            onChange={(values) => {
              if (values.length > selectedUsersForMention.length) {
                // User was added
                const newUserId = values[values.length - 1];
                handleMentionSelect(newUserId);
              }
            }}
            disabled={disabled}
            showSearch
            optionFilterProp="children"
            listHeight={400}
            virtual={true}
            filterOption={(input, option) => {
              const user = filteredUsers.find((u) => u.id === option?.value);
              if (!user) return false;
              const searchText = `${user.firstName} ${user.lastName} ${user.username}`.toLowerCase();
              return searchText.includes(input.toLowerCase());
            }}
          >
            {availableUsers.map((user) => {
              const roleLabel = getRoleLabel(user);
              return (
                <Option key={user.id} value={user.id}>
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {user.firstName} {user.lastName}
                      {roleLabel && (
                        <span style={{
                          marginLeft: 8,
                          fontStyle: 'italic',
                          color: '#999',
                          fontWeight: 'normal',
                          fontSize: 12
                        }}>
                          ({roleLabel})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      @{user.username}
                    </div>
                  </div>
                </Option>
              );
            })}
          </Select>
          <HelpText>
            Type to search for users to mention. They will be notified.
          </HelpText>
        </div>
      )}

      {mentionedUsers.length > 0 && (
        <MentionedUsersContainer>
          <div style={{ fontSize: 12, color: '#666', marginRight: 8 }}>
            Mentioned:
          </div>
          {mentionedUsers.map((user) => (
            <Tag
              key={user.id}
              closable={!disabled}
              onClose={() => handleRemoveMention(user.id)}
              color="blue"
            >
              @{user.firstName} {user.lastName}
            </Tag>
          ))}
        </MentionedUsersContainer>
      )}
    </div>
  );
};

export default MentionTextArea;
